import {
	REPORTED_EXCEPTION,
	SearchFormModel,
	SearchModel,
	webapi,
	DateUtils,
	NumberUtils
} from 'ps-gui';
import removeAccents from 'remove-accents';

/** @module utils */

export function downloadFile(fileId, fileName, type) {
	const contentType = 'application/octet-stream';
	const contentDisposition = 'attachment; filename="' + encodeRFC5987ValueChars(fileName) + '"';
	document.getElementById('downloadFrame').src = webapi.getDecoratedUrl('/api/file/' + fileId) + '?content_type=' + encodeURIComponent(contentType) + '&content_disposition=' + encodeURIComponent(contentDisposition) + '&type=' + encodeURIComponent(type);
}

/**
 * Prevadi seznam hodnot primitivnich typu na mnozinu techto hodnot
 * @param {Immutable.List|Array} list seznam primitivnich typu
 * @returns {Object} vraci objekt, jehoz klici jsou polozky ze vstupniho seznamu a hodnotami true - jedna se tedy o mnozinu tvorenou polozkami z vstupniho seznamu
 */
export function listToSet(list) {
	let res = {};
	if (list) {
		list.forEach(item => {
			res[item] = true;
		});
	}
	return res;
}

/**
 * Encoduje vstupni retezec podle RFC5987. Pouziva se k encodovani UTF-8 nazvu souboru pro pouziti v hlavicce content-disposition
 * @param {String} str vstupni retezec
 * @returns vraci vstupni retezec encodovany podle RFC5987
 */
function encodeRFC5987ValueChars(str) {
	return (
		encodeURIComponent(str)
			// Note that although RFC3986 reserves "!", RFC5987 does not,
			// so we do not need to escape it
			.replace(/['()*]/g, c => `%${c.charCodeAt(0).toString(16)}`) // i.e., %27 %28 %29 %2a (Note that valid encoding of "*" is %2A
			// which necessitates calling toUpperCase() to properly encode)
			// The following are not required for percent-encoding per RFC5987,
			// so we can allow for a little better readability over the wire: |`^
			.replace(/%(7C|60|5E)/g, (str, hex) =>
				String.fromCharCode(parseInt(hex, 16))
			)
	);
}

export async function exportToCsv(model, options) {
	options = options || {};
	let filter = model.getFullSearchFilter(true);
	const url = model.getUri();
	filter.firstRow = 1;
	filter.maxRowsPerPage = 99 * 1000 * 1000;
	const resultModel = model.getResultModel();
	if (resultModel) {
		filter._columnTextMap = Object.assign({}, resultModel.getColumnTextMap(), options.columnTextMap);
	}
	if (options.outputColumns) {
		filter._output = options.outputColumns;
	}
	let res = await webapi.execute(url + '/csv', filter);
	if (res !== REPORTED_EXCEPTION) {
		downloadFile(res, 'export.csv');
	}
}

export function createSearchModel(url) {
	return new SearchModel().setUri(url)
		.setResultSetModuleId('PortalResultSet')
		.setMaxRowsPerPage(16)
		.setCsvExportCallback(exportToCsv)
		.setForm(new SearchFormModel());
}

/*
 * parsuje retezec ve formatu partyId/roleTypeId
 * do objektu {partyId, roleTypeId}
 */
export function parseRoleId(roleId) {
	if (!roleId) {
		return null;
	}
	let idx = roleId.indexOf('/');
	return {
		partyId: parseInt(roleId.substring(0, idx)),
		roleTypeId: roleId.substring(idx + 1)
	};
}

const twoDecFmt = NumberUtils.getFixedDecimalFormatNumber(2);
export function formatFileSize(bytes) {
	if (bytes >= 1024 * 1024) {
		return twoDecFmt(bytes / 1024.0 / 1024.0) + ' MB';
	} else if (bytes >= 1024) {
		return twoDecFmt(bytes / 1024.0) + ' kB';
	} else {
		return bytes + ' B';
	}
}

export function stripDia(value) {
	if (!value) {
		return value;
	}
	return removeAccents(value);
}

export function getSqlFulltextFilter(value, autoWildCard = false) {
	if (value) {
		// normalize dates
		value = value.replace(/([0-9]{1,2})[.,/][\s]*([0-9]{1,2})[.,/][\s]*([0-9]{4})/g, '$1.$2.$3');
		// - = *
		value = value.replace(/-/g, '*');
	}
	value = stripDia(value);
	let tokens = [].concat.apply([], (value || '').split('"').map((v, i) => {
		return i % 2 ? '"' + v + '"' : v.split(/[^\w*.]+/g);
	})).filter(Boolean);
	tokens = tokens.map(token => {
		const date = DateUtils.parseDate(token);
		const astIdx = token.indexOf('*');
		if (astIdx === token.length - 1) {
			return '"' + token + '"';
		} else if (!DateUtils.isInvalidDate(date)) {
			return 'D' + DateUtils.toRawDate(date);
		} else {
			return token;
		}
	}).filter(token => !!token);
	if (autoWildCard && value && value.endsWith(tokens[tokens.length - 1])) {
		tokens[tokens.length - 1] += '%';
	}
	return tokens.map(token => '"' + token + '"').join(' and ').replace(/\*/g, '%');
}

const htmlFormatCharMap = {
	'<': '&lt;',
	'>': '&gt;',
	'*': {
		tag: 'b'
	}
};

/**
 * Escapes html tags. Formats string with markup in type of *xxx* to become
 * html tag <>xxx</>. It helps content to be used with DOM.
 * !!! Is very sensitive to value given. Replaces also every random <|> in
 * text, where it should not be html tag. For markup replaces every * in text
 * for current b tag. Could cause possible errors.
 * !! Every <|> character escapes to &lt;|&gt; but for markup and puts <|> as not escaped character.
 * @export
 * @param {String} value input content
 * @return {String} content with escaped HTML and markuped values for DOM
 */
export function formatHtml(value) {
	if (typeof value !== 'string') {
		return null;
	}
	let openTags = {};
	let res = [];
	let strtIdx = 0;
	const len = value.length;
	for (let i = 0; i < len; i++) {
		let c = value.charAt(i);
		let replaceWith = htmlFormatCharMap[c];
		if (replaceWith) {
			if (i > strtIdx) {
				res.push(value.substring(strtIdx, i));
			}
			strtIdx = i + 1;
			if (typeof replaceWith === 'string') {
				res.push(replaceWith);
			} else {
				const tag = replaceWith.tag;
				if (openTags[tag]) {
					res.push('</');
					res.push(tag);
					res.push('>');
					openTags[tag] = false;
				} else {
					res.push('<');
					res.push(tag);
					res.push('>');
					openTags[tag] = true;
				}
			}
		} else if (c === '[') {
			if (value.charAt(i + 1) === '[') {
				i += 2;
				let endIdx = value.indexOf(']]', i);
				if (endIdx > i) {
					if (strtIdx < i - 2) {
						res.push(value.substring(strtIdx, i - 2));
					}
					let link = value.substring(i, endIdx);
					let href, text;
					let titleIdx = link.lastIndexOf('|');
					if (titleIdx < 0) {
						href = link;
						text = link;
					} else {
						href = link.substring(0, titleIdx);
						text = link.substring(titleIdx + 1);
					}
					res.push('<a href="');
					res.push(escapeHtml(href));
					res.push('" target="_blank">');
					res.push(escapeHtml(text));
					res.push('</a>');
					i = endIdx + 1;
					strtIdx = i + 1;
				}
			}
		}
	}
	if (strtIdx < value.length) {
		res.push(value.substring(strtIdx));
	}
	return res.join('');
}

function escapeHtml(value) {
	if (typeof value === 'string') {
		return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/, '&gt;');
	} else {
		return value;
	}
}

/**
 * Formats phone number given from database in format +xxxyyyzzzbbb. Other formats are not present in database.
 * !!! Sensitive to value given. Made to format czech syntax given from database.
 * !! If value|string is too short, still puts empty space behind.
 * @export
 * @param {String} value given number
 * @return {String} formatted number in format +xxx yyy zzz bbb
 */
export function formatPhoneNumber(value) {
	if (!value || typeof value !== 'string') {
		return '';
	}
	return value.substring(0, 4) + ' ' + value.substring(4, 7) + ' ' + value.substring(7, 10) + ' ' + value.substring(10);
}

/**
 * @typedef {Object} PasswordValidationResult
 * @property {boolean} valid je heslo validni?
 * @property {boolean} min8Chars obsahuje heslo alespon 8 znaku?
 * @property {boolean} upperLower obsahuje alespon jeden upper a jeden lowercase znak
 * @property {boolean} specialChars obsahuje alespon jeden nealpha znak
 */

/**
 * Provede validaci hesla
 * @param {String} pwd heslo k validaci
 * @returns {PasswordValidationResult}
 */
export function validatePassword(pwd) {
	let res = {};
	if (!pwd) {
		return res;
	}
	const dialess = stripDia(pwd);
	res.min8Chars = pwd.length >= 8;
	res.upperLower = dialess.toLowerCase() !== dialess && dialess.toUpperCase() !== dialess;
	res.specialChars = /[^a-zA-Z]/.test(dialess);
	res.valid = res.min8Chars && res.upperLower && res.specialChars;
	return res;
}

/**
 * Provede normalizaci tel. cisla:
 * 	Odstrani neciselne znaky.
 * 	Pokud se jedna o 9 znake cislo bez predvolby doplni '+420'.
 * 	Jinak doplni/ponecha '+'.
 * @param {String} value telefonni cislo
 * @returns {String} normalizovane telefonni cislo
 */
export function normalizePhoneNumber(value) {
	if (!value || typeof value !== 'string') {
		return '';
	}
	const isComplete = value.startsWith('+');
	value = value.replace(/[^0-9]/g, '');
	if (value.length === 9 && !isComplete) {
		return '+420' + value;
	} else {
		return '+' + value;
	}
}

export function getGlobalContextChangeChecker(module) {
	return async function checkGlobalContextChange(prevProps) {
		const prevGlobalContext = prevProps ? prevProps.globalContext : null;
		if (prevGlobalContext === module.props.globalContext) {
			return;
		}
		const model = module.getModel();
		if (model.checkGlobalContextChange) {
			const newModel = await model.checkGlobalContextChange(module.props.globalContext);
			if (newModel !== model) {
				module.setModel(newModel);
			}
		}
	};
}

export function wait(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

